import React, { useState, useRef, useEffect } from 'react';
import { ShortsMode, ReframeMode, SHORTS_TIP, DEFAULT_SHORTS_MODE } from '../utils/shorts';
import {
  InfoTip,
  TranscriptionFields,
  ShortsModePicker,
  ReframeModePicker,
  SummaryVideoOption,
  useWhisperModels,
} from './ShortsSetupFields';
import ExistingVideoPicker, { ExistingVideo } from './ExistingVideoPicker';
import KineticCaptionPicker, { KineticCaptionSettings } from './KineticCaptionPicker';

interface CreateMediaModalProps {
  open: boolean;
  onClose: () => void;
  /** `kineticCaption` is passed only when the Kinetic Caption tab is switched on. */
  onUploadVideo: (file: File, name: string, description: string, language: string, whisperModel: string, createSummary?: boolean, mode?: ShortsMode, reframeMode?: ReframeMode, kineticCaption?: KineticCaptionSettings) => void;
  onFromImages: (name: string, description: string) => void;
  onFromLink: (linkUrl: string, name: string, description: string, language: string, whisperModel: string, mode?: ShortsMode, reframeMode?: ReframeMode, kineticCaption?: KineticCaptionSettings) => void;
  /** Project videos already run through a job — lets the user regenerate shorts from one without re-uploading. */
  onUseExistingVideo?: (videoId: string, options?: { name?: string; description?: string; kineticCaption?: KineticCaptionSettings; mode?: ShortsMode; reframeMode?: ReframeMode; createSummary?: boolean }) => void;
  projectId?: string;
  uploading?: boolean;
  uploadOnly?: boolean;
}

type SourceType = 'upload' | 'images' | 'link' | 'existing' | null;
type ModalTab = 'general' | 'kinetic';

const deriveNameFromLink = (rawUrl: string): string => {
  try {
    const parsed = new URL(rawUrl);
    const host = parsed.hostname.replace(/^www\./, '');
    const pathTail = parsed.pathname.split('/').filter(Boolean).pop();
    if (pathTail) {
      return `${host}_${decodeURIComponent(pathTail).slice(0, 60)}`;
    }
    return host || 'linked_video';
  } catch {
    return 'linked_video';
  }
};

const CreateMediaModal: React.FC<CreateMediaModalProps> = ({
  open,
  onClose,
  onUploadVideo,
  onFromImages,
  onFromLink,
  onUseExistingVideo,
  projectId,
  uploading,
  uploadOnly = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState<SourceType>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [language, setLanguage] = useState('auto');
  const { models: whisperModels, model: whisperModel, setModel: setWhisperModel } = useWhisperModels(open);
  const [createSummary, setCreateSummary] = useState(false);
  const [mode, setMode] = useState<ShortsMode>(DEFAULT_SHORTS_MODE);
  const [reframeMode, setReframeMode] = useState<ReframeMode>('active-speaker');
  const [selectedVideo, setSelectedVideo] = useState<ExistingVideo | null>(null);
  const [tab, setTab] = useState<ModalTab>('general');
  const [kineticCaption, setKineticCaption] = useState<KineticCaptionSettings | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canUseExisting = Boolean(!uploadOnly && projectId && onUseExistingVideo);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setSource('upload');
      setLinkUrl('');
      setSelectedFile(null);
      setLanguage('auto');
      setMode(DEFAULT_SHORTS_MODE);
      setReframeMode('active-speaker');
      setCreateSummary(false);
      setSelectedVideo(null);
      setTab('general');
      setKineticCaption(null);
    }
  }, [open, uploadOnly]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const activeSource: SourceType = uploadOnly ? 'upload' : source;
  const hasName = Boolean(name.trim() || (activeSource === 'link' && linkUrl.trim()) || activeSource === 'existing');
  const needsModelAndLanguage = activeSource === 'upload' || activeSource === 'link';
  // Captions apply only to shorts jobs (upload / link / use existing), not to Publish or Story.
  const showTabs = (needsModelAndLanguage || activeSource === 'existing') && !uploadOnly;
  const activeTab: ModalTab = showTabs ? tab : 'general';
  const hasModelAndLanguage = Boolean(language.trim() && whisperModel.trim());
  const canProceed = Boolean(
    hasName &&
    activeSource &&
    (activeSource !== 'link' || linkUrl.trim()) &&
    (activeSource !== 'existing' || selectedVideo) &&
    (!needsModelAndLanguage || hasModelAndLanguage)
  );

  const handleProceed = () => {
    if (!canProceed) return;
    if (activeSource === 'upload') {
      if (selectedFile) {
        onUploadVideo(selectedFile, name.trim(), description.trim(), language, whisperModel, createSummary, mode, reframeMode, kineticCaption ?? undefined);
      } else {
        fileInputRef.current?.click();
      }
    } else if (activeSource === 'images') {
      onFromImages(name.trim(), description.trim());
    } else if (activeSource === 'link') {
      const fallbackName = deriveNameFromLink(linkUrl.trim());
      onFromLink(linkUrl.trim(), name.trim() || fallbackName, description.trim(), language, whisperModel, mode, reframeMode, kineticCaption ?? undefined);
    } else if (activeSource === 'existing') {
      if (selectedVideo) {
        onUseExistingVideo?.(selectedVideo.id, {
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          kineticCaption: kineticCaption ?? undefined,
          mode,
          reframeMode,
          createSummary,
        });
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!name.trim()) {
        setName(file.name.replace(/\.[^.]+$/, ''));
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container create-media-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h2>{uploadOnly ? 'Publish Video' : 'Create Shorts - Quick Action'}</h2>
            {!uploadOnly && <p className="modal-subtitle">Fastest way to generate a shorts</p>}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {showTabs && (
          <div className="cm-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={activeTab === 'general'} className={`cm-tab ${activeTab === 'general' ? 'cm-tab--active' : ''}`} onClick={() => setTab('general')}>General</button>
            <button type="button" role="tab" aria-selected={activeTab === 'kinetic'} className={`cm-tab ${activeTab === 'kinetic' ? 'cm-tab--active' : ''}`} onClick={() => setTab('kinetic')}>
              Kinetic Caption{kineticCaption && <span className="cm-tab-dot" aria-label="on" />}
            </button>
          </div>
        )}

        {activeTab === 'kinetic' && (
          <KineticCaptionPicker value={kineticCaption} onChange={setKineticCaption} />
        )}

        {activeTab === 'general' && (<>
        {canUseExisting && (
          <div className="opt-row-group opt-row-group--horizontal">
            <label className={`opt-row ${activeSource === 'upload' ? 'opt-row--active' : ''}`}>
              <input type="radio" name="cm-video-source" checked={activeSource === 'upload'} onChange={() => setSource('upload')} />
              <span className="opt-row-body"><span className="opt-row-title">Upload a new video</span></span>
            </label>
            <label className={`opt-row ${activeSource === 'existing' ? 'opt-row--active' : ''}`}>
              <input type="radio" name="cm-video-source" checked={activeSource === 'existing'} onChange={() => setSource('existing')} />
              <span className="opt-row-body"><span className="opt-row-title">Use an already-uploaded video</span></span>
            </label>
          </div>
        )}

        {activeSource === 'existing' && projectId && (
          <div className="modal-field">
            <label>Video</label>
            <ExistingVideoPicker
              projectId={projectId}
              selectedId={selectedVideo?.id || null}
              onSelect={setSelectedVideo}
              requireTranscript
            />
            <div className="cm-field-help">
              Regenerates shorts for this video using the options below — no re-upload needed. The result is added as its own item.
            </div>
          </div>
        )}

        <>
            <div className="modal-field">
              <label>Name{activeSource === 'existing' && <span style={{ opacity: 0.5, fontWeight: 400 }}> (optional)</span>}</label>
              <input
                type="text"
                placeholder={activeSource === 'existing' ? 'Defaults to "<video name> (copy)"' : 'Enter a name for your media...'}
                value={name}
                onChange={e => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="modal-field">
              <label>Description <span style={{ opacity: 0.5, fontWeight: 400 }}>(optional)</span></label>
              <textarea
                placeholder="Describe your media..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                style={{ resize: 'vertical' }}
              />
            </div>
        </>

        {(activeSource === 'upload' || activeSource === 'link') && !uploadOnly && (
          <TranscriptionFields
            language={language}
            onLanguageChange={setLanguage}
            model={whisperModel}
            onModelChange={setWhisperModel}
            models={whisperModels}
            helpText="Used only for this media job."
          />
        )}

        {(needsModelAndLanguage || activeSource === 'existing') && !uploadOnly && (
          <ShortsModePicker value={mode} onChange={setMode} name="cm-shorts-mode" horizontal />
        )}

        {(needsModelAndLanguage || activeSource === 'existing') && !uploadOnly && (
          <ReframeModePicker value={reframeMode} onChange={setReframeMode} name="cm-reframe-mode" />
        )}

        {(activeSource === 'upload' || activeSource === 'existing') && !uploadOnly && (
          <SummaryVideoOption checked={createSummary} onChange={setCreateSummary} />
        )}

        {activeSource === 'link' && (
          <div className="modal-field">
            <label>Link URL</label>
            <input
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
            />
            <div className="cm-field-help">Supported providers: YouTube, X/Twitter, Instagram, Facebook.</div>
          </div>
        )}

        {activeSource === 'upload' && (
          <div className="modal-field">
            <label>Video File <InfoTip text={SHORTS_TIP.videoFile} /></label>
            <div
              className="cm-file-drop"
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedFile ? (
                <span className="cm-file-name">🎬 {selectedFile.name}</span>
              ) : (
                <span className="cm-file-placeholder">Click to select a video file...</span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        )}
        </>)}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!canProceed || (activeSource === 'upload' && !selectedFile) || uploading}
            onClick={handleProceed}
          >
            {uploading ? '⏳ Uploading...' : (uploadOnly ? 'Upload & Publish →' : (activeSource === 'existing' ? 'Generate Shorts →' : 'Proceed →'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateMediaModal;
